import type { OperationError, OperationResult } from '../../../domain';

/**
 * Thrown by `useOperationMutation` when the BE returns an `OperationResult`
 * with `status: 'Failed'` or `'Cancelled'`. The HTTP request itself
 * succeeded (envelope `success: true`), but the upstream provider /
 * orchestrator rejected the work. Carrying a typed class — instead of a
 * generic Error — lets panels distinguish a BE-reported operation failure
 * from a network / validation throw, and gives them direct access to the
 * structured `OperationError` (code, message, retryable) for richer UI
 * (e.g. a Retry button for `retryable: true` errors).
 */
export class OperationFailedError extends Error {
  readonly result: OperationResult;
  readonly operationError: OperationError | null;
  readonly code: string;
  readonly retryable: boolean;

  constructor(result: OperationResult) {
    const message =
      result.error?.message?.trim() ||
      `Operation ${result.status.toLowerCase()} without a message.`;
    super(message);
    this.name = 'OperationFailedError';
    this.result = result;
    this.operationError = result.error ?? null;
    this.code = result.error?.code ?? 'OPERATION_FAILED';
    this.retryable = result.error?.retryable ?? false;
  }
}
